import { cn } from "@/lib/utils";

export const Label = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-navy">
    {children} {required && <span className="text-ocean">*</span>}
  </label>
);

export const ErrorText = ({ children }) =>
  children ? <p className="mt-1 text-xs font-medium text-red-500" data-testid="field-error">{children}</p> : null;

const baseInput =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-ocean focus:ring-4 focus:ring-ocean/10";

export const TextField = ({ label, required, error, id, ...props }) => (
  <div>
    {label && <Label htmlFor={id} required={required}>{label}</Label>}
    <input id={id} className={cn(baseInput, error && "border-red-400")} {...props} />
    <ErrorText>{error}</ErrorText>
  </div>
);

export const TextArea = ({ label, required, error, id, rows = 4, ...props }) => (
  <div>
    {label && <Label htmlFor={id} required={required}>{label}</Label>}
    <textarea id={id} rows={rows} className={cn(baseInput, "resize-none", error && "border-red-400")} {...props} />
    <ErrorText>{error}</ErrorText>
  </div>
);

export const SelectField = ({ label, required, error, id, options, placeholder = "Select...", ...props }) => (
  <div>
    {label && <Label htmlFor={id} required={required}>{label}</Label>}
    <select id={id} className={cn(baseInput, "appearance-none bg-[right_1rem_center] bg-no-repeat", error && "border-red-400")} {...props}
      style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%230A192F' stroke-width='2'><path d='M4 6l4 4 4-4'/></svg>\")" }}>
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
    <ErrorText>{error}</ErrorText>
  </div>
);

export const CheckGroup = ({ label, options, values, onToggle, columns = 2, testId }) => (
  <div>
    {label && <Label>{label}</Label>}
    <div className={cn("grid gap-2", columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3")} data-testid={testId}>
      {options.map((o) => {
        const active = values.includes(o);
        return (
          <button
            type="button"
            key={o}
            onClick={() => onToggle(o)}
            data-testid={`check-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={cn(
              "flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors duration-200",
              active ? "border-ocean bg-ocean/5 text-navy" : "border-line bg-white text-navy/70 hover:border-ocean/40"
            )}
          >
            <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border", active ? "border-ocean bg-ocean text-white" : "border-slate-300")}>
              {active && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
            </span>
            {o}
          </button>
        );
      })}
    </div>
  </div>
);

export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
